import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { ViewspecState } from "../../viewspecs/types.ts";
import { DEFAULT_VIEWSPECS } from "../../viewspecs/types.ts";
import { containsText } from "../../documents/search.ts";
import { isDescendantOf } from "../../documents/addressing.ts";

export const viewspecKey = new PluginKey<ViewspecState>("viewspecs");

/**
 * ProseMirror plugin that applies viewspec decorations.
 * Controls visibility, SID display, indentation, and filtering
 * without modifying the underlying document.
 */
export function viewspecPlugin(
  initialViewspecs: ViewspecState = DEFAULT_VIEWSPECS
): Plugin {
  return new Plugin({
    key: viewspecKey,

    state: {
      init(): ViewspecState {
        return { ...initialViewspecs };
      },
      apply(tr, value): ViewspecState {
        const meta = tr.getMeta(viewspecKey) as
          | Partial<ViewspecState>
          | undefined;
        if (meta) return { ...value, ...meta };
        return value;
      },
    },

    props: {
      decorations(state) {
        const vs = viewspecKey.getState(state);
        if (!vs) return DecorationSet.empty;

        const decorations: Decoration[] = [];
        let sequentialIndex = 0;

        state.doc.descendants((node, pos) => {
          if (node.type.name !== "statement") return;

          const level = node.attrs.level as number;
          const sid = node.attrs.sid as string;

          // --- Depth filtering ---
          if (vs.depthLimit !== null && level > vs.depthLimit) {
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, {
                class: "nls-hidden",
                style: "display: none",
              })
            );
            return false; // don't descend
          }

          // --- Frozen view ---
          if (vs.showFrozen) {
            const frozenSid = vs.showFrozen;
            const isTarget = sid === frozenSid;
            const isDescendant = isDescendantOf(sid, frozenSid);
            const isAncestor = isDescendantOf(frozenSid, sid);
            if (!isTarget && !isDescendant && !isAncestor) {
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  class: "nls-hidden",
                  style: "display: none",
                })
              );
              return false;
            }
          }

          // --- Text filter ---
          if (vs.filterText) {
            const text = node.textContent;
            if (!text.toLowerCase().includes(vs.filterText.toLowerCase())) {
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, {
                  class: "nls-hidden",
                  style: "display: none",
                })
              );
              return false;
            }
          }

          // --- Indentation ---
          const indent = vs.indentation ? (level - 1) * 2 : 0;
          const blankLineMargin = vs.showBlankLines ? "0.5em" : "0";

          decorations.push(
            Decoration.node(pos, pos + node.nodeSize, {
              style: `padding-left: ${indent}rem; margin-bottom: ${blankLineMargin}`,
            })
          );

          // --- SID widget ---
          if (vs.showSIDs) {
            decorations.push(
              Decoration.widget(
                pos + 1,
                () => {
                  const span = document.createElement("span");
                  span.className = "nls-sid";
                  if (vs.numbering === "sid") {
                    span.textContent = `(${sid}) `;
                  } else if (vs.numbering === "sequential") {
                    span.textContent = `${sequentialIndex + 1}. `;
                  }
                  return span;
                },
                { side: -1 }
              )
            );
          } else if (vs.numbering === "sequential") {
            decorations.push(
              Decoration.widget(
                pos + 1,
                () => {
                  const span = document.createElement("span");
                  span.className = "nls-sid";
                  span.textContent = `${sequentialIndex + 1}. `;
                  return span;
                },
                { side: -1 }
              )
            );
          }

          // --- Content truncation ---
          if (vs.showContent === "none") {
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, {
                class: "nls-statement nls-content-hidden",
                style: `padding-left: ${indent}rem; margin-bottom: ${blankLineMargin}`,
              })
            );
          } else if (vs.showContent === "first-line") {
            decorations.push(
              Decoration.node(pos, pos + node.nodeSize, {
                class: "nls-first-line-only",
              })
            );
          }

          sequentialIndex++;
        });

        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}
